import styles from 'css/src/components/common/ui/AnimationUpsellBanner.module.scss';

import React, { useCallback, useRef } from 'react';

import {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    IconButton,
    PrimaryButton,
} from 'js/src/components/common/ui/Button';

export type AnimationUpsellThumbnail = {
    src: string;
    alt: string;
};

export const ANIMATION_UPSELL_THUMBNAILS: AnimationUpsellThumbnail[] = [
    {
        src: 'https://storage.googleapis.com/lumen5-prod-images/genx-banner-waving-3s.mp4',
        alt: 'Animated character',
    },
    {
        src: 'https://storage.googleapis.com/lumen5-prod-images/genx-banner-coins-cropped-3s.mp4',
        alt: 'Animated coins',
    },
    {
        src: 'https://storage.googleapis.com/lumen5-prod-images/genx-banner-apple.mp4',
        alt: 'Animated apple',
    },
];

const Thumbnail: React.FC<AnimationUpsellThumbnail> = ({ src, alt }) => (
    <video
        className={styles['thumbnail']}
        src={src}
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        disableRemotePlayback
        aria-label={alt}
    />
);

export type AnimationUpsellBannerProps = {
    header?: string;
    description?: React.ReactNode;
    ctaText?: string;
    ctaIconRight?: string;
    onCtaClick: () => void;
    /** When omitted, the banner renders without a dismiss button (non-dismissable). */
    onDismiss?: () => void;
    isVisible?: boolean;
    className?: string;
    compact?: boolean;
};

const AnimationUpsellBanner: React.FC<AnimationUpsellBannerProps> = ({
    header = 'New feature! Animated explainer',
    description = 'These videos are well-suited for ideas that are difficult to show in the real world.',
    ctaText = 'Try now',
    ctaIconRight = 'chevron_right',
    onCtaClick,
    onDismiss,
    isVisible = true,
    className,
    compact = false,
}) => {
    const bannerRef = useRef<HTMLDivElement>(null);

    const handleMouseEnter = useCallback(() => {
        const videos = bannerRef.current?.querySelectorAll('video');
        videos?.forEach((video) => {
            video.loop = true;
            video.play().catch(() => {});
        });
    }, []);

    const handleMouseLeave = useCallback(() => {
        const videos = bannerRef.current?.querySelectorAll('video');
        videos?.forEach((video) => {
            video.pause();
        });
    }, []);

    if (!isVisible) {
        return null;
    }

    return (
        <div
            ref={bannerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={onCtaClick}
            className={`${styles['banner']} ${compact ? styles['banner--compact'] : ''} ${
                onDismiss ? styles['banner--dismissable'] : ''
            } ${className ?? ''}`}
        >
            <div className={`d-flex align-items-center gap-2 ${styles['thumbnails']}`}>
                {ANIMATION_UPSELL_THUMBNAILS.map((thumb) => (
                    <Thumbnail key={thumb.src} src={thumb.src} alt={thumb.alt} />
                ))}
            </div>
            <div className={`d-flex flex-column justify-content-center ${styles['copy']}`}>
                <div className="font-l5 font-l5--small--semibold">{header}</div>
                <div className="font-l5 font-l5--subtext color-grey-800">{description}</div>
            </div>
            {/* stopPropagation prevents the banner's onClick from firing twice when the button is clicked */}
            <div
                className={`d-flex align-items-center ${styles['cta']}`}
                onClick={(e) => e.stopPropagation()}
            >
                <PrimaryButton
                    text={ctaText}
                    iconRight={ctaIconRight}
                    onClick={onCtaClick}
                    size={BUTTON_SIZES.SM}
                />
            </div>
            {/* stopPropagation prevents dismiss from triggering the banner CTA */}
            {onDismiss && (
                <div
                    className={`d-flex align-items-center ${styles['dismiss']}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <IconButton
                        size={BUTTON_SIZES.SM}
                        variant={BUTTON_VARIANTS.LINK}
                        color={BUTTON_COLORS.SIMPLE}
                        onClick={onDismiss}
                        icon="close"
                        aria-label="Dismiss"
                    />
                </div>
            )}
        </div>
    );
};

export default AnimationUpsellBanner;
